import { useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  Building2,
  Database,
  Plug,
  PlayCircle,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Activity,
  Clock,
  Zap,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useCreateOnboardingWorkspace,
  useForceStartHeartbeat,
  useOnboardingStatus,
  useStartOnboardingAutoSetup,
} from '@/hooks/useOnboardingStatus'
import { useResolvedWorkspace } from '@/hooks/useWorkspaces'
import type { OnboardingPreset, VisibleWorkspaceInfo } from '@/types/api'

// ─── ステップ定義 ───

type Step = 'workspace' | 'datasource' | 'guide' | 'autostart' | 'done'

const STEPS: { id: Step; label: string; icon: React.ElementType }[] = [
  { id: 'workspace', label: 'ワークスペース', icon: Building2 },
  { id: 'datasource', label: 'データソース', icon: Database },
  { id: 'guide', label: '接続ガイド', icon: Plug },
  { id: 'autostart', label: '自動開始確認', icon: PlayCircle },
]

const STEP_ORDER: Step[] = ['workspace', 'datasource', 'guide', 'autostart', 'done']

function stepIndex(s: Step): number {
  return STEP_ORDER.indexOf(s)
}

// ─── ステップ: ワークスペース作成 ───

interface WorkspaceStepProps {
  manageableWorkspaces: VisibleWorkspaceInfo[]
  hasMemberOnlyWorkspace: boolean
  selectedWorkspaceId: string
  onSelectWorkspace: (workspaceId: string) => void
  onUseExistingWorkspace: () => void
  onNext: (slug: string, name: string) => Promise<void>
}

function WorkspaceStep({
  manageableWorkspaces,
  hasMemberOnlyWorkspace,
  selectedWorkspaceId,
  onSelectWorkspace,
  onUseExistingWorkspace,
  onNext,
}: WorkspaceStepProps) {
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const hasManageableWorkspace = manageableWorkspaces.length > 0

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!slug.trim()) { setError('スラッグを入力してください'); return }
    if (!name.trim()) { setError('会社名を入力してください'); return }
    if (!/^[a-z0-9-]+$/.test(slug)) { setError('スラッグは小文字英数字とハイフンのみ使用できます'); return }
    setError('')
    setLoading(true)
    try {
      await onNext(slug.trim(), name.trim())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ワークスペースの作成に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">ワークスペースを選択または作成</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          管理できる既存ワークスペースを使うか、新しいワークスペースを作成して進められます。
        </p>
      </div>

      {hasManageableWorkspace && (
        <div className="space-y-4 rounded-xl border bg-muted/30 p-4">
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-foreground">既存のワークスペースを使う</h3>
            <label htmlFor="onboarding-workspace-select" className="block text-sm font-medium">利用するワークスペース</label>
            <select
              id="onboarding-workspace-select"
              value={selectedWorkspaceId}
              onChange={(e) => onSelectWorkspace(e.target.value)}
              className="block w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {manageableWorkspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={onUseExistingWorkspace}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            このワークスペースで続ける <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {hasMemberOnlyWorkspace && !hasManageableWorkspace && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          既存ワークスペースは表示されていますが、この画面を進めるには admin 権限が必要です。
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border p-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">新しいワークスペースを作成</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            別顧客用の box を追加したい場合はこちらを使います。
          </p>
        </div>
        <div>
          <label htmlFor="onboarding-workspace-slug" className="block text-sm font-medium">スラッグ（URL識別子）</label>
          <input
            id="onboarding-workspace-slug"
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="my-company"
            className="mt-1 block w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="mt-1 text-xs text-muted-foreground">小文字英数字とハイフンのみ（例: my-company）</p>
        </div>
        <div>
          <label htmlFor="onboarding-workspace-name" className="block text-sm font-medium">会社名</label>
          <input
            id="onboarding-workspace-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="株式会社〇〇"
            className="mt-1 block w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {loading ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> 作成中...</>
          ) : (
            <>次へ <ChevronRight className="h-4 w-4" /></>
          )}
        </button>
      </form>
    </div>
  )
}

// ─── ステップ: データソース選択 ───

const DATA_SOURCES = [
  { id: 'line', label: 'LINE', description: '社内LINEグループのメッセージを収集' },
  { id: 'fieldy', label: 'Fieldy', description: '現場作業データを収集' },
  { id: 'google', label: 'Google Workspace', description: 'Gmail / Google Drive を収集' },
  { id: 'chatwork', label: 'Chatwork', description: 'Chatwork のメッセージを収集' },
]

interface DatasourceStepProps {
  onNext: (selected: string[]) => void
}

function DatasourceStep({ onNext }: DatasourceStepProps) {
  const [selected, setSelected] = useState<string[]>([])

  function toggle(id: string) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">データソースを選択</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          収集したいデータソースを選択してください（複数選択可）。
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {DATA_SOURCES.map((ds) => {
          const isSelected = selected.includes(ds.id)
          return (
            <button
              key={ds.id}
              onClick={() => toggle(ds.id)}
              className={cn(
                'rounded-lg border p-4 text-left transition-colors',
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              )}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{ds.label}</span>
                {isSelected && <CheckCircle2 className="h-4 w-4 text-primary" />}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{ds.description}</p>
            </button>
          )
        })}
      </div>
      <div className="flex gap-3">
        <button
          onClick={() => onNext(selected)}
          disabled={selected.length === 0}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          次へ <ChevronRight className="h-4 w-4" />
        </button>
        <button
          onClick={() => onNext([])}
          className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          スキップ
        </button>
      </div>
    </div>
  )
}

// ─── ステップ: 接続ガイド ───

interface GuideStepProps {
  dataSources: string[]
  onNext: () => void
}

function GuideStep({ dataSources, onNext }: GuideStepProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">接続ガイド</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          選択したアダプターの接続手順を確認してください。
        </p>
      </div>
      <div className="space-y-4">
        {dataSources.length === 0 ? (
          <p className="rounded-lg border p-4 text-sm text-muted-foreground">
            データソースが選択されていません。後から「Adapters」メニューで設定できます。
          </p>
        ) : (
          dataSources.map((id) => (
            <div key={id} className="rounded-lg border p-4">
              <h3 className="font-medium capitalize">{id} の接続手順</h3>
              <ol className="mt-2 list-decimal space-y-1 pl-4 text-sm text-muted-foreground">
                <li>左メニューの「Adapters」を開く</li>
                <li>{id.toUpperCase()} を選択して「Connect」をクリック</li>
                <li>認証情報を入力して接続完了</li>
              </ol>
            </div>
          ))
        )}
      </div>
      <button
        onClick={onNext}
        className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        次へ <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  )
}

// ─── ステップ: 自動開始確認 ───

interface AutoStartStepProps {
  onStart: (preset: OnboardingPreset) => Promise<void>
}

function AutoStartStep({ onStart }: AutoStartStepProps) {
  const [preset, setPreset] = useState<OnboardingPreset>('general')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleStart() {
    setLoading(true)
    setError('')
    try {
      await onStart(preset)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'セットアップの開始に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">自動開始の確認</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          業種に合ったテンプレートプリセットを選択してセットアップを完了します。
        </p>
      </div>
      <div className="space-y-3">
        {([
          { value: 'general', label: '汎用', desc: '業種を問わず使えるビルトインテンプレート' },
          { value: 'manufacturing', label: '製造業', desc: '工事進捗・安全管理・設備保守向け' },
          { value: 'recruitment', label: '人材業', desc: '採用候補者・取引先管理向け（GearChange型）' },
        ] as const).map((p) => (
          <button
            key={p.value}
            onClick={() => setPreset(p.value)}
            className={cn(
              'w-full rounded-lg border p-4 text-left transition-colors',
              preset === p.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            )}
          >
            <div className="flex items-center justify-between">
              <span className="font-medium">{p.label}</span>
              {preset === p.value && <CheckCircle2 className="h-4 w-4 text-primary" />}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{p.desc}</p>
          </button>
        ))}
      </div>
      <button
        onClick={handleStart}
        disabled={loading}
        className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        {loading ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> セットアップ中...</>
        ) : (
          <><PlayCircle className="h-4 w-4" /> セットアップ開始</>
        )}
      </button>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

// ─── ステップ: 完了 + ステータス表示 ───

interface DoneStepProps {
  workspaceId: string
}

function DoneStep({ workspaceId }: DoneStepProps) {
  const { data: status, isLoading, isError } = useOnboardingStatus(workspaceId)
  const forceStart = useForceStartHeartbeat(workspaceId)

  const hoursRemaining =
    status?.hoursElapsed != null
      ? Math.max(0, 24 - status.hoursElapsed)
      : null

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center space-y-3 py-4 text-center">
        <CheckCircle2 className="h-14 w-14 text-green-500" />
        <h2 className="text-2xl font-semibold">セットアップ完了！</h2>
        <p className="max-w-sm text-sm text-muted-foreground">
          ワークスペースの準備が整いました。データが蓄積されると AI が自動的に提案を生成します。
        </p>
      </div>

      {/* ステータスカード */}
      <div className="rounded-xl border bg-muted/30 p-4">
        <h3 className="mb-3 text-sm font-semibold text-foreground">オンボーディング進捗</h3>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>ステータスを取得中...</span>
          </div>
        )}

        {isError && (
          <p className="text-sm text-destructive">ステータスの取得に失敗しました。</p>
        )}

        {status && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="flex flex-col items-center rounded-lg border bg-card p-3 text-center">
              <Plug className="mb-1 h-5 w-5 text-primary" />
              <span className="text-2xl font-bold">{status.connectedAdapters}</span>
              <span className="mt-0.5 text-xs text-muted-foreground">接続済みアダプター</span>
            </div>

            <div className="flex flex-col items-center rounded-lg border bg-card p-3 text-center">
              <Database className="mb-1 h-5 w-5 text-primary" />
              <span className="text-2xl font-bold">{status.documentCount.toLocaleString()}</span>
              <span className="mt-0.5 text-xs text-muted-foreground">蓄積ドキュメント</span>
            </div>

            <div className="flex flex-col items-center rounded-lg border bg-card p-3 text-center">
              <Clock className="mb-1 h-5 w-5 text-primary" />
              <span className="text-2xl font-bold">
                {hoursRemaining != null ? `${hoursRemaining}h` : '—'}
              </span>
              <span className="mt-0.5 text-xs text-muted-foreground">AI 開始まで</span>
            </div>

            <div className="flex flex-col items-center rounded-lg border bg-card p-3 text-center">
              <Activity className="mb-1 h-5 w-5 text-primary" />
              <span
                className={cn(
                  'text-sm font-semibold',
                  status.heartbeatEnabled ? 'text-green-600' : 'text-muted-foreground'
                )}
              >
                {status.heartbeatEnabled ? '有効' : '無効'}
              </span>
              <span className="mt-0.5 text-xs text-muted-foreground">Heartbeat</span>
            </div>
          </div>
        )}

        {/* heartbeat 未有効時の「今すぐ開始」ボタン */}
        {status && !status.heartbeatEnabled && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/30">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Heartbeat が無効です。データが蓄積されても AI の自律巡回が行われません。
            </p>
            <button
              className="mt-2 flex items-center gap-2 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              disabled={forceStart.isPending}
              onClick={() => forceStart.mutate()}
            >
              {forceStart.isPending ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" />開始中...</>
              ) : (
                <><Zap className="h-3.5 w-3.5" />今すぐ開始</>
              )}
            </button>
            {forceStart.isError && (
              <p className="mt-1 text-xs text-red-600">開始に失敗しました。再試行してください。</p>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-center">
        <a
          href="/"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          ダッシュボードへ
        </a>
      </div>
    </div>
  )
}

// ─── メインコンポーネント ───

export function OnboardingPage() {
  const resolvedWorkspace = useResolvedWorkspace(import.meta.env.VITE_DEFAULT_WORKSPACE_ID)
  const createWorkspace = useCreateOnboardingWorkspace()
  const [step, setStep] = useState<Step>('workspace')
  const [dataSources, setDataSources] = useState<string[]>([])
  const [workspaceId, setWorkspaceId] = useState<string>('')
  const [workspaceName, setWorkspaceName] = useState<string>('')
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('')

  const manageableWorkspaces = useMemo(
    () => resolvedWorkspace.workspaces.filter((workspace) => workspace.role === 'admin'),
    [resolvedWorkspace.workspaces]
  )
  const hasMemberOnlyWorkspace =
    resolvedWorkspace.workspaces.some((workspace) => workspace.role !== 'admin')

  useEffect(() => {
    const preferredManageableWorkspaceId = manageableWorkspaces[0]?.id ?? ''
    if (!preferredManageableWorkspaceId) {
      if (selectedWorkspaceId) {
        setSelectedWorkspaceId('')
      }
      return
    }
    const selectedIsManageable = manageableWorkspaces.some((workspace) => workspace.id === selectedWorkspaceId)
    if (!selectedIsManageable) {
      setSelectedWorkspaceId(preferredManageableWorkspaceId)
    }
  }, [manageableWorkspaces, selectedWorkspaceId])

  const startAutoSetup = useStartOnboardingAutoSetup(workspaceId)

  const selectedVisibleWorkspace = useMemo(
    () => manageableWorkspaces.find((workspace) => workspace.id === selectedWorkspaceId) ?? null,
    [manageableWorkspaces, selectedWorkspaceId]
  )

  async function handleWorkspaceNext(slug: string, name: string) {
    const created = await createWorkspace.mutateAsync({ slug, name })
    setWorkspaceId(created.workspaceId)
    setWorkspaceName(created.name)
    setStep('datasource')
  }

  function handleUseExistingWorkspace() {
    if (!selectedVisibleWorkspace) return
    setWorkspaceId(selectedVisibleWorkspace.id)
    setWorkspaceName(selectedVisibleWorkspace.name)
    setStep('datasource')
  }

  function handleDatasourceNext(selected: string[]) {
    setDataSources(selected)
    setStep('guide')
  }

  function handleGuideNext() {
    setStep('autostart')
  }

  async function handleAutoStart(preset: OnboardingPreset) {
    if (!workspaceId) {
      throw new Error('ワークスペースが未選択です')
    }
    const result = await startAutoSetup.mutateAsync(preset)
    if (!result.started || !result.pipelineOk) {
      throw new Error('テンプレート適用または自動開始に失敗しました')
    }
    setStep('done')
  }

  const currentIndex = stepIndex(step)

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      {/* ページタイトル */}
      <div>
        <h1 className="text-2xl font-bold">新規オンボーディング</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          ステップに従って設定を完了してください
        </p>
        {workspaceId && (
          <p className="mt-2 text-xs text-muted-foreground">
            設定対象: {workspaceName || workspaceId}
          </p>
        )}
      </div>

      {resolvedWorkspace.error && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">利用可能なワークスペースの取得に失敗しました。</p>
            <p className="mt-1 text-xs">
              ログイン状態または API 接続を確認してください。必要であれば新規作成フローに進めます。
            </p>
          </div>
        </div>
      )}

      {/* ステップインジケーター */}
      {step !== 'done' && (
        <nav className="flex items-center gap-2">
          {STEPS.map((s, i) => {
            const done = stepIndex(s.id) < currentIndex
            const active = s.id === step
            return (
              <div key={s.id} className="flex items-center gap-2">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium',
                    done
                      ? 'bg-green-500 text-white'
                      : active
                      ? 'bg-primary text-primary-foreground'
                      : 'border bg-background text-muted-foreground'
                  )}
                >
                  {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                </div>
                <span
                  className={cn(
                    'hidden text-xs sm:block',
                    active ? 'font-medium text-foreground' : 'text-muted-foreground'
                  )}
                >
                  {s.label}
                </span>
                {i < STEPS.length - 1 && (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            )
          })}
        </nav>
      )}

      {/* ステップコンテンツ */}
      <div className="rounded-xl border bg-card p-6 shadow-sm">
        {step === 'workspace' && (
          <WorkspaceStep
            manageableWorkspaces={manageableWorkspaces}
            hasMemberOnlyWorkspace={hasMemberOnlyWorkspace}
            selectedWorkspaceId={selectedWorkspaceId}
            onSelectWorkspace={setSelectedWorkspaceId}
            onUseExistingWorkspace={handleUseExistingWorkspace}
            onNext={handleWorkspaceNext}
          />
        )}
        {step === 'datasource' && <DatasourceStep onNext={handleDatasourceNext} />}
        {step === 'guide' && <GuideStep dataSources={dataSources} onNext={handleGuideNext} />}
        {step === 'autostart' && <AutoStartStep onStart={handleAutoStart} />}
        {step === 'done' && <DoneStep workspaceId={workspaceId} />}
      </div>
    </div>
  )
}
